import { InputSummary } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';
import {v4 as uuid} from 'uuid';
import AnessaListItemUtil, { AnessaListItem } from '../../anessa-list-item.interface';
import { PeriodType } from '../plant-specifications/fuel.model';


export interface FinancialOpexInterface {
  name: string;
  uuid: string;
  description: string;
  type: string;
  cost: UnitValue;
  connectionType: string;
  period: UnitValue;
  frequencyOfOccurrence: UnitValue;
  duration: UnitValue;
  cpi: UnitValue;
  estimated: boolean;
  estimationPercentage: UnitValue;
}

export class FinancialOpex implements AnessaListItem {
  name: string;
  uuid: string;
  description: string;
  type: string;
  cost: UnitValue;
  connectionType: string;
  period: UnitValue;
  frequencyOfOccurrence: UnitValue;
  duration: UnitValue;
  cpi: UnitValue;
  estimated: boolean;
  estimationPercentage: UnitValue;

  constructor(param: FinancialOpexInterface = {
    name: '',
    uuid: null,
    description: '',
    type: null,
    cost: new UnitValue(),
    connectionType: '',
    period: new UnitValue(),
    frequencyOfOccurrence: new UnitValue(),
    duration: new UnitValue(),
    cpi: new UnitValue(),
    estimated: false,
    estimationPercentage: new UnitValue()
  }) {
    this.name = param.name;
    // this.uuid = param.uuid ? param.uuid : uuid();
    if (param.uuid) {
      this.uuid = param.uuid;
    } else {
      this.uuid = uuid();
    }
    this.description = param.description;
    this.type = param.type;
    this.cost = new UnitValue(param.cost);
    this.connectionType = param.connectionType ? param.connectionType : PeriodType.PERIOD;
    this.period = new UnitValue(param.period);
    this.frequencyOfOccurrence = new UnitValue(param.frequencyOfOccurrence);
    this.duration = new UnitValue(param.duration);
    this.cpi = new UnitValue(param.cpi);
    this.estimated = param.estimated;
    this.estimationPercentage = new UnitValue(param.estimationPercentage);
  }

  static deepCopy(opex: FinancialOpex) {
    let copy;
    if (opex) {
      copy = new FinancialOpex(opex);
      return copy;
    }
    throw new Error('opex deep copy failed');
  }

  /**
   * Returns a copy of the provided array of opex while maintaining their types.
   * @param opex is an array of FinancialOpex objects.
   */
  static arrayDeepCopy(opex: FinancialOpex[]) {
    const copy: FinancialOpex[] = [];
    if (opex && opex.length) {
      for (const o of opex) {
        copy.push(new FinancialOpex(o));
      }
    }
    return copy;
  }

  getInputSummary(path: string[], opex: FinancialOpex) :InputSummary {
    const inputSummary : InputSummary= new InputSummary();
    inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FinOpexPropName.cost, this.cost, [...path, 'cost']);
    if (opex.type === FinancialOpexType.MAJOR_MAINT) {
    inputSummary.inputs = InputSummaryUtils.performPush(
    inputSummary.inputs,
    FinOpexPropName.frequencyOfOccurrence,
    this.frequencyOfOccurrence,
    [...path, 'frequencyOfOccurrence']
    );
    } else {
    inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FinOpexPropName.period, this.period, [...path, 'period']);
    }
    inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FinOpexPropName.duration, this.duration, [...path, 'duration']);
    inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FinOpexPropName.cpi, this.cpi, [...path, 'cpi']);
    inputSummary.inputs = InputSummaryUtils.performPush(inputSummary.inputs, FinOpexPropName.connectionType, this.connectionType, [...path, 'connectionType']);
    return inputSummary;
  }

  getUuidForPath() {
    return AnessaListItemUtil.getUuidForPath(this.uuid);
  }
}

export enum FinOpexPropName {
  name = 'INPUT_SUMMARY.FINANCIAL.NAME',
  type = 'INPUT_SUMMARY.FINANCIAL.OPEX.TYPE',
  cost = 'INPUT_SUMMARY.FINANCIAL.COST',
  period = 'INPUT_SUMMARY.FINANCIAL.QUANTITY',
  connectionType = "INPUT_SUMMARY.FINANCIAL.OPEX.CONNECTION_TYPE",
  frequencyOfOccurrence = 'INPUT_SUMMARY.FINANCIAL.OPEX.FREQUENCY_OF_OCCURRENCE',
  duration = 'INPUT_SUMMARY.FINANCIAL.OPEX.DURATION',
  cpi = 'INPUT_SUMMARY.FINANCIAL.CPI',
  id_name = 'name',
  id_type = 'type',
  id_cost = 'cost',
  id_period = 'period',
  id_frequencyOfOccurrence = 'frequency of occurrence',
  id_duration = 'duration of opex',
  id_cpi = 'CPI',
}

export enum FinancialOpexType {
  FEEDSTOCK = 'feedstock expense',
  DIGESTATE = 'digestate expense',
  FUEL = 'consumed fuel expense',
  LABOUR = 'labour',
  INSURANCE = 'insurance',
  MAINT = 'maintenance',
  CONSUMABLES = 'consumables',
  OTHER = 'other',
  MAJOR_MAINT = 'major maintenance',
}
